import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, GitCompare, RotateCcw } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { useAuthStore } from '@/stores/auth'
import type {
  PromptHistoryEntry,
  PromptHistoryResponse,
  PromptResponse,
} from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * `/admin/prompts/:id/history` — timeline of versions with diff + activate
 * actions (Story 3.7 §AC #8). Older-than-active activations issue a
 * `prompt.rolled_back` audit on the server; the UI confirms via window.confirm
 * before sending the request to avoid accidental rollbacks.
 */
export function PromptHistoryPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const canMutate = role === 'admin' || role === 'manager'
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const { data: master } = useQuery<PromptResponse>({
    queryKey: ['admin-prompts', id],
    queryFn: async () => api<PromptResponse>(`/admin/prompts/${id ?? ''}`),
    enabled: id !== undefined,
  })

  const { data, isLoading, isError } = useQuery<PromptHistoryResponse>({
    queryKey: ['admin-prompts', id, 'history'],
    queryFn: async () => api<PromptHistoryResponse>(`/admin/prompts/${id ?? ''}/history`),
    enabled: id !== undefined,
  })

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const activateMutation = useMutation({
    mutationFn: async (version: number) =>
      api<PromptResponse>(`/admin/prompts/${id ?? ''}/activate/${version}`, {
        method: 'POST',
        body: JSON.stringify({ rationale: `Ativação manual da versão v${version}` }),
      }),
    onSuccess: (res) => {
      notify({
        variant: 'default',
        title: `Versão v${res.prompt.version} ativada`,
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao ativar', description: msg })
    },
  })

  const activeVersion = master?.prompt.version

  function handleActivate(entry: PromptHistoryEntry): void {
    if (!canMutate || activeVersion === undefined) return
    if (entry.version === activeVersion) return
    const isRollback = entry.version < activeVersion
    const confirmMsg = isRollback
      ? `Tem certeza? Esta é uma operação de ROLLBACK. A versão atual (v${activeVersion}) será desativada e a v${entry.version} (mais antiga) voltará a ser usada.`
      : `Ativar versão v${entry.version}? A versão atual (v${activeVersion}) será desativada.`
    if (!window.confirm(confirmMsg)) return
    activateMutation.mutate(entry.version)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label="Voltar">
          <Link to={`/admin/prompts/${id ?? ''}`}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Histórico — {master?.prompt.campaignName ?? '...'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Cada edição cria uma versão imutável. Clique em "Diff" para comparar e em "Ativar"
            para promovê-la.
          </p>
        </div>
      </div>

      <ol className="space-y-3">
        {isLoading && (
          <li className="rounded-md border p-4 text-sm text-muted-foreground">Carregando…</li>
        )}
        {isError && (
          <li className="rounded-md border p-4 text-sm text-destructive">
            Erro ao carregar histórico.
          </li>
        )}
        {!isLoading && !isError && (data?.history ?? []).length === 0 && (
          <li className="rounded-md border p-4 text-sm text-muted-foreground">
            Sem histórico para este prompt.
          </li>
        )}
        {(data?.history ?? []).map((entry: PromptHistoryEntry) => {
          const isCurrent = entry.version === activeVersion
          return (
            <li
              key={entry.id}
              className="grid gap-2 rounded-md border bg-card p-4 sm:grid-cols-[auto_1fr_auto] sm:items-start sm:gap-4"
            >
              <div className="flex items-center gap-2">
                <Badge variant={isCurrent ? 'success' : 'secondary'}>v{entry.version}</Badge>
                {isCurrent && <span className="text-xs text-muted-foreground">ativa</span>}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {entry.fromVersion === null
                    ? 'Versão inicial'
                    : `Transição v${entry.fromVersion} → v${entry.toVersion}`}
                </p>
                <p className="text-xs text-muted-foreground">{entry.rationale}</p>
                <p className="text-[11px] text-muted-foreground">
                  {new Date(entry.createdAt).toLocaleString('pt-BR')}
                </p>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Ver corpo
                  </summary>
                  <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-2 font-mono">
                    {entry.systemPrompt}
                  </pre>
                </details>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={`/admin/prompts/${id ?? ''}/history/${entry.version}`}
                    aria-label={`Ver diff de v${entry.version}`}
                  >
                    <GitCompare className="h-4 w-4" aria-hidden="true" />
                    Diff
                  </Link>
                </Button>
                {canMutate && !isCurrent && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleActivate(entry)}
                    disabled={activateMutation.isPending}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Ativar
                  </Button>
                )}
              </div>
            </li>
          )
        })}
      </ol>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

export default PromptHistoryPage
